"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The Turnstile widget, as a hook and a component, so every form gets the same
 * render, reset, teardown and failure handling instead of its own copy.
 *
 * The public site key is inlined at build time. When it is unset, which is the
 * normal state locally, the widget is skipped entirely and the server route
 * skips verification to match. That is deliberate: a form that cannot be
 * submitted on a developer's machine is a form nobody tests.
 *
 * WHY THIS TRACKS A STATUS AND NOT JUST A TOKEN (2026-09-14).
 *
 * Tom opened /free/stop-doing in desktop Safari and the widget sat on
 * "Verifying..." forever. The site's configuration was not the cause: the same
 * page, key and CSP issue a token instantly in a normal Chrome, real Safari on
 * the same Mac moments later got Cloudflare's "Verify you are human" box rather
 * than a stall, the widget's hostnames are right in Cloudflare, and the CSP
 * allows challenges.cloudflare.com. A challenge that never finishes is something
 * Cloudflare does to some visits (tracking protection, VPNs and relays, content
 * blockers, automation, a bad moment on their side), and nothing on our side
 * can force it through.
 *
 * What WAS ours was the page around it. The forms only knew "token or no token",
 * so the button stayed live, the visitor pressed it, and was told to "complete
 * the check below" about a check that sat above the message and could not be
 * completed. Somebody trying to give us their email address had no way forward.
 *
 * So the hook now reports what the check is doing, notices when it has stalled,
 * failed or never loaded, offers a retry, and `TurnstileCheck` turns that into
 * plain words with an email address as the way out. Buttons stay disabled until
 * the check passes.
 *
 * It also no longer uses the script's `?onload=` global. That was one function
 * slot shared by every widget on a page, so a second form overwrote the first
 * form's callback and the first widget never rendered. The hook waits for
 * `window.turnstile` itself.
 */
export const TURNSTILE_SITE_KEY =
  process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim() || undefined;

/** Where somebody goes when the check will not let them through. */
export const TURNSTILE_FALLBACK_EMAIL = "team@ownlydigital.co.uk";

/** A healthy check finishes in a second or two. Past this, say something. */
const STUCK_AFTER_MS = 12_000;
/** How long to wait for Cloudflare's script before assuming it was blocked. */
const SCRIPT_TIMEOUT_MS = 15_000;

/** Loads the Turnstile script, or nothing at all when there is no key. */
export function TurnstileScript() {
  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <Script
      src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      strategy="afterInteractive"
    />
  );
}

export type TurnstileStatus =
  /** No site key, so there is no check and nothing to wait for. */
  | "off"
  /** Waiting for Cloudflare, either the script or the challenge itself. */
  | "verifying"
  /**
   * Cloudflare wants a click ("Verify you are human"). Not stuck: waiting on the
   * visitor, so no stall timer runs. Real Safari on Tom's Mac lands here.
   */
  | "interactive"
  /** A token is in hand. */
  | "passed"
  /** Still verifying well past the time a healthy check takes. */
  | "stuck"
  /** Cloudflare reported an error or the challenge timed out. */
  | "failed"
  /** The script never arrived, which is nearly always a blocker. */
  | "unavailable";

export type Turnstile = {
  /**
   * Pass as the `ref` of the element the widget renders into. A callback rather
   * than a ref object, so a container that mounts late (a multi-step form's last
   * step) still gets a widget, and so nothing reads a ref during render.
   */
  mountWidget: (el: HTMLDivElement | null) => void;
  /** Null until the visitor has passed, and again after every reset. */
  token: string | null;
  status: TurnstileStatus;
  /** True when a submission can go: there is no check, or it has passed. */
  ready: boolean;
  /**
   * True while the check is still expected to finish by itself or with a click.
   * Buttons say "Waiting for the security check" only then; once it has stalled or
   * failed, the message next to the widget explains and the button keeps its name.
   */
  waiting: boolean;
  /**
   * Call in a submit handler's `finally`. A Turnstile token is single use, so
   * without this a second attempt after a failure silently fails verification
   * and the person is told their details are wrong.
   */
  reset: () => void;
  /** Start the check again after it stalled or failed. */
  retry: () => void;
};

export function useTurnstile(
  /**
   * `interactionOnly`: Cloudflare draws nothing unless it needs the person to tick a box, so a
   * form that must fit a phone's first screen (the DM resource pages) carries no empty widget.
   */
  options: { interactionOnly?: boolean } = {},
): Turnstile {
  const interactionOnly = options.interactionOnly === true;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(
    TURNSTILE_SITE_KEY ? "verifying" : "off",
  );

  const mountWidget = useCallback((el: HTMLDivElement | null) => setContainer(el), []);

  const clearStuckTimer = useCallback(() => {
    if (stuckTimerRef.current) clearTimeout(stuckTimerRef.current);
    stuckTimerRef.current = null;
  }, []);

  const armStuckTimer = useCallback(() => {
    clearStuckTimer();
    stuckTimerRef.current = setTimeout(() => {
      setStatus((s) => (s === "verifying" ? "stuck" : s));
    }, STUCK_AFTER_MS);
  }, [clearStuckTimer]);

  /** Render into `el` if Cloudflare's script is here. True once there is a widget. */
  const renderInto = useCallback(
    (el: HTMLDivElement): boolean => {
      if (!TURNSTILE_SITE_KEY || !window.turnstile) return false;
      if (widgetIdRef.current) return true;
      try {
        widgetIdRef.current = window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          ...(interactionOnly ? { appearance: "interaction-only" as const } : {}),
          callback: (t) => {
            clearStuckTimer();
            setToken(t);
            setStatus("passed");
          },
          "error-callback": () => {
            clearStuckTimer();
            setToken(null);
            setStatus("failed");
            // Handled: we show our own message, so Turnstile need not throw.
            return true;
          },
          "expired-callback": () => {
            // Tokens last five minutes. The widget fetches a fresh one by itself.
            setToken(null);
            setStatus("verifying");
            armStuckTimer();
          },
          "timeout-callback": () => {
            clearStuckTimer();
            setToken(null);
            setStatus("failed");
          },
          "unsupported-callback": () => {
            clearStuckTimer();
            setToken(null);
            setStatus("unavailable");
          },
          "before-interactive-callback": () => {
            clearStuckTimer();
            setStatus((s) => (s === "passed" ? s : "interactive"));
          },
          "after-interactive-callback": () => {
            // Clicked. Cloudflare finishes off, and a stall from here is a real stall.
            setStatus((s) => (s === "interactive" ? "verifying" : s));
            armStuckTimer();
          },
        });
      } catch {
        clearStuckTimer();
        setStatus("unavailable");
      }
      return true;
    },
    [armStuckTimer, clearStuckTimer, interactionOnly],
  );

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !container) return;
    const started = Date.now();
    // Every attempt runs from a timer, never synchronously in the effect body, so a
    // render that fails straight away cannot set state mid-commit.
    const poll = setInterval(tick, 200);
    const first = setTimeout(tick, 0);
    function tick() {
      if (renderInto(container as HTMLDivElement)) {
        clearInterval(poll);
        // The stall clock starts once there is a challenge to stall. Before that, a
        // missing script is its own case, reported by the timeout below.
        if (widgetIdRef.current) armStuckTimer();
      } else if (Date.now() - started > SCRIPT_TIMEOUT_MS) {
        clearInterval(poll);
        clearStuckTimer();
        setStatus((s) => (s === "passed" ? s : "unavailable"));
      }
    }
    return () => {
      clearInterval(poll);
      clearTimeout(first);
      clearStuckTimer();
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* noop */
        }
      }
      widgetIdRef.current = null;
    };
  }, [container, renderInto, armStuckTimer, clearStuckTimer]);

  const reset = useCallback(() => {
    setToken(null);
    if (!TURNSTILE_SITE_KEY || !window.turnstile || !widgetIdRef.current) return;
    setStatus("verifying");
    armStuckTimer();
    try {
      window.turnstile.reset(widgetIdRef.current);
    } catch {
      /* noop */
    }
  }, [armStuckTimer]);

  const retry = useCallback(() => {
    if (!TURNSTILE_SITE_KEY) return;
    setToken(null);
    setStatus("verifying");
    armStuckTimer();
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        return;
      } catch {
        widgetIdRef.current = null;
      }
    }
    if (!container || !renderInto(container)) {
      clearStuckTimer();
      setStatus("unavailable");
    }
  }, [armStuckTimer, clearStuckTimer, container, renderInto]);

  const ready = !TURNSTILE_SITE_KEY || (status === "passed" && token !== null);
  const waiting = !ready && (status === "verifying" || status === "interactive");

  return { mountWidget, token, status, ready, waiting, reset, retry };
}

/**
 * Words for a submit attempted before the check has passed. A backstop only: the
 * buttons are disabled until then, but Enter in a field can still call submit.
 */
export function turnstileNotReadyMessage(status: TurnstileStatus): string {
  if (status === "verifying") {
    return "The security check is still running. It normally takes a second or two, so please give it a moment.";
  }
  if (status === "interactive") {
    return "Please tick the Cloudflare box to confirm you are a person, then try again.";
  }
  return `The security check has not let you through yet. Please try it again, or email us at ${TURNSTILE_FALLBACK_EMAIL}.`;
}

/**
 * The widget plus a line saying what it is doing. Render this where the widget
 * should appear and pass the hook's return value in.
 */
export function TurnstileCheck({
  turnstile,
  className = "",
  quiet = false,
}: {
  turnstile: Turnstile;
  className?: string;
  /**
   * For forms that must fit a phone's first screen (the DM resource pages): no reserved height and
   * no "running a check" or "check complete" lines. Anything that needs the person (a box to tick,
   * a failure) still shows, because that is the only time they need to know the check exists.
   */
  quiet?: boolean;
}) {
  if (!TURNSTILE_SITE_KEY) return null;
  const { status, retry, mountWidget } = turnstile;
  const trouble = status === "stuck" || status === "failed" || status === "unavailable";
  /* One structure either way, so the widget's element is never swapped out from under it. */
  return (
    <div className={className}>
      <div
        ref={mountWidget}
        /* color-scheme normal: the page root is `dark`, and a browser paints an opaque canvas
           behind any iframe whose document's scheme differs from its parent's. Cloudflare's
           frame does not declare one, so keep its container on the default and it stays
           transparent exactly as before. */
        className={quiet ? "[color-scheme:normal]" : "min-h-[65px] [color-scheme:normal]"}
        role="group"
        aria-label="Security check"
        hidden={status === "unavailable"}
      />
      <div aria-live="polite" className="mt-2 text-sm leading-[22px]">
        {status === "verifying" && !quiet ? (
          <p className="text-white/60">
            Cloudflare is running a quick check to filter out bots. It usually takes a second
            or two.
          </p>
        ) : null}

        {status === "interactive" ? (
          <p className="text-white/72">Tick the Cloudflare box to confirm you are a person.</p>
        ) : null}

        {status === "passed" && !quiet ? <p className="text-white/60">Check complete, thank you.</p> : null}

        {trouble ? (
          <div
            role="alert"
            className="rounded-xl border border-[#E5C077]/30 bg-[#E5C077]/[0.08] px-4 py-3 text-copy leading-[23px] text-[#F0D9A8]"
          >
            <p>
              {status === "stuck"
                ? "The security check is taking longer than it should."
                : status === "failed"
                  ? "The security check could not finish."
                  : "The security check did not load. A content blocker or a strict privacy setting is usually the reason."}
            </p>
            <p className="mt-2">
              {status === "unavailable" ? (
                <>Refresh the page to try again</>
              ) : (
                <button
                  type="button"
                  onClick={retry}
                  className="font-semibold text-white underline decoration-white/40 underline-offset-2 transition hover:decoration-white"
                >
                  Try the check again
                </button>
              )}
              , or email{" "}
              <a
                href={`mailto:${TURNSTILE_FALLBACK_EMAIL}`}
                className="font-semibold text-white underline decoration-white/40 underline-offset-2 transition hover:decoration-white"
              >
                {TURNSTILE_FALLBACK_EMAIL}
              </a>{" "}
              and we will sort it out for you.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
